import { CalendarFilled, VerticalAlignTopOutlined } from '@ant-design/icons'
import { UploadDownloadButtons } from '@components/_SiteWide/UploadStatus/UploadDownloadButtons'
import { GraviButton, Horizontal, onExport, Texto, Vertical } from '@gravitate-js/excalibrr'
import { Dropdown, Popover, Slider } from 'antd'
import classNames from 'classnames'
import { addDays, subDays } from 'date-fns'
import moment from 'moment/moment'
import React, { useMemo, useState } from 'react'
import { DateRangePicker } from 'react-date-range'

export function ResourcePlannerActionButtons({
  setCurrentFilters,
  currentFilters,
  dayRangeStorage,
  filterDayFormat,
  setDayRangeStorage,
  gridAPIRef,
}) {
  const [visible, setVisible] = useState(false)
  const isSet = useMemo(() => !!currentFilters?.from, [currentFilters?.from])
  const maxDate = useMemo(() => moment(currentFilters.from).add(30, 'days').toDate(), [currentFilters?.from])
  const minDate = useMemo(() => moment(currentFilters.to).subtract(30, 'days').toDate(), [currentFilters?.to])

  const dateChange = (item) => {
    if (item.selection.startDate === null || item.selection.endDate === null) return
    if (moment(item.selection.endDate).diff(moment(item.selection.startDate), 'days') >= 31) return

    const newFilters = {
      ...currentFilters,
      from: moment(item.selection.startDate).format(filterDayFormat),
      to: moment(item.selection.endDate).format(filterDayFormat),
    }

    setCurrentFilters(newFilters)
  }

  const dateFilters = [
    {
      startDate: currentFilters?.from && new Date(currentFilters.from),
      endDate: currentFilters?.to && new Date(currentFilters.to),
      key: 'selection',
    },
  ]
  const commonProps = {
    onChange: (item) => dateChange(item),
    showSelectionPreview: true,
    moveRangeOnFirstSelection: false,
    months: 2,
    ranges: dateFilters,
    direction: 'horizontal',
    minDate,
    maxDate,
    dragSelectionEnabled: false,
  }

  const menu = (
    <>
      <DefaultSlider
        setCurrentFilters={setCurrentFilters}
        dayRangeStorage={dayRangeStorage}
        setDayRangeStorage={setDayRangeStorage}
        filterDayFormat={filterDayFormat}
      />

      <DateRangePicker {...commonProps} />
    </>
  )
  const excelOptions = [
    {
      title: 'Download Transportation Resource Planner',
      onClick: () =>
        onExport(gridAPIRef, {
          fileName: `TransportationResourcePlanner${moment().format('YYYY-MM-DD')}.xlsx`,
          sheetName: 'Transportation Resource Planner',
        }),
    },
  ]
  return (
    <Horizontal gap={16} verticalCenter>
      <UploadDownloadButtons excelOptions={excelOptions} />
      <Popover content={menu} onOpenChange={(flag) => setVisible(flag)} open={visible} placement='bottomRight'>
        <GraviButton
          size='large'
          icon={<CalendarFilled />}
          buttonText={
            isSet
              ? `${dateFilters[0]?.startDate?.toLocaleDateString()} - ${dateFilters[0]?.endDate?.toLocaleDateString()}`
              : 'Filter By Date'
          }
          theme2={isSet}
          onClick={(e) => e.preventDefault()}
        />
      </Popover>
    </Horizontal>
  )
}

function DefaultSlider({ setCurrentFilters, dayRangeStorage, setDayRangeStorage, filterDayFormat, maxDayRange = 31 }) {
  const [sliderValues, setSliderValues] = useState([
    dayRangeStorage.from || dayRangeStorage.from === 0 ? parseInt(dayRangeStorage.from) : -7,
    dayRangeStorage.to || dayRangeStorage.to === 0 ? parseInt(dayRangeStorage.to) : 23,
  ])
  const applyChanges = (setInStorage = true, values) => {
    if (!values) values = [sliderValues[0], sliderValues[1]]
    if (setInStorage) {
      setDayRangeStorage({ from: values[0], to: values[1] })
    }
    setCurrentFilters((currentFilters) => ({
      ...currentFilters,
      from: moment(subDays(new Date(), Math.abs(values[0]))).format(filterDayFormat),
      to: moment(addDays(new Date(), values[1])).format(filterDayFormat),
    }))
  }

  const sliderChange = (value) => {
    if (value[0] === 0 && value[1] === 0) return
    if (value[0] <= 0 && value[1] >= 0 && Math.abs(value[0]) + Math.abs(value[1]) < maxDayRange) {
      setSliderValues(value)
    }
  }
  const resetChanges = () => {
    setSliderValues([-7, 23])
    applyChanges(true, [-7, 23])
  }

  const setDefault = () => {}

  return (
    <Horizontal alignItems='flex-end' style={{ backgroundColor: '#E1EEFF', padding: '0 1em 1em' }}>
      <Vertical flex={3}>
        <GraviButton
          style={{
            border: 'none',
            boxShadow: 'none',
            backgroundColor: '#E1EEFF',

            maxWidth: 'fit-content',
            paddingLeft: 0,
          }}
          buttonText='DEFAULT WINDOW'
          icon={<VerticalAlignTopOutlined style={{ transform: 'rotate(-90deg)' }} />}
          onClick={setDefault}
        />
        <Texto>Use this setting to load a tailored grid view every time.</Texto>
      </Vertical>

      <Horizontal verticalCenter flex={4} horizontalCenter>
        <Texto className='mt-3' appearance='secondary' weight='bold'>
          <VerticalAlignTopOutlined className='mr-2' style={{ transform: 'rotate(-90deg)' }} />
          {sliderValues[0] === null
            ? ''
            : sliderValues[0] === -1
              ? `${Math.abs(sliderValues[0])} day back`
              : `${Math.abs(sliderValues[0])} days back`}
        </Texto>
        <Slider
          range
          className='date-range-filter-slider mx-4'
          defaultValue={sliderValues}
          value={sliderValues}
          max={31}
          min={-31}
          style={{ width: '200px', marginBottom: 0, marginTop: '1em' }}
          marks={{
            0: {
              style: {
                transform: 'translate(-50%, -160%)',
                color: 'var(--gray-400)',
              },
              label: <strong>0</strong>,
            },
          }}
          onChange={sliderChange}
        />
        <Texto className='mt-3' appearance='secondary' weight='bold'>
          <VerticalAlignTopOutlined className='mr-2' style={{ transform: 'rotate(90deg)' }} />
          {sliderValues[1] === 1 ? `${sliderValues[1]} day ahead` : `${sliderValues[1]} days ahead`}
        </Texto>
      </Horizontal>
      <Vertical alignItems='space-between' style={{ maxWidth: 'fit-content', padding: '1em 0.5em 0' }}>
        <GraviButton
          buttonText='Reset'
          theme4='true'
          style={{ maxWidth: 'fit-content', marginBottom: '0.5em', height: 'fit-content', padding: '2px 10px' }}
          onClick={resetChanges}
        />
        <GraviButton
          buttonText='Apply'
          theme2
          style={{ width: 'fit-content', height: 'fit-content', padding: '2px 10px' }}
          onClick={applyChanges}
        />
      </Vertical>
    </Horizontal>
  )
}
